import * as React from "react";
import { Action } from "./Action.js";
import { OTRS } from "./OTRS.js";
import * as cls from "./Accessibility.module.scss";
export { Accessibility };
const fontList = [1, 1.25, 1.5];
const fontLabel = ["100 процентов", "125 процентов", "150 процентов"];
const fontAbbr = ["100%", "125%", "150%"];
const fgList = ["#3B7CBE", "black", "#657B83", "#839496", "white"];
const bgList = ["#EAF0F5", "white", "#FDF6E3", "#002B36", "black"];
const colorLabel = ["Цветная", "Белая", "Светлая", "Тёмная", "Чёрная"];
const colorAbbr = ["Ц", "Б", "С", "Т", "Ч"];
/**
 * Отображает панель "Средства доступности" (в режиме доступности),
 * или содержимое элемента (в обычном режиме). 
 * Туда помещают основное меню для обычного режима.
 * @summary Панель Средства доступности.
 * @returns {JSX.Element} Элемент JSX.
 * @example
 * <Accessibility/>
 * @author Алексей Васьковский <avaskovskiy@umney.ru>
 */
function Accessibility({ home = "https://roweb.online/personal" }) {
  const HOME = new Action(
    {
      name: "Домой",
      icon: "home",
      href: home
    });
  const menu = [HOME, ...Accessibility.menu, OTRS.OPEN, Accessibility.CLOSE];
  const [active, setActive] = React.useState(parseInt(localStorage.getItem(
    "personal-studio-components.accessibility.active") || "0"));
  const [font, setFont] = React.useState(parseInt(localStorage.getItem(
    "personal-studio-components.accessibility.font") || "0"));
  const [color, setColor] = React.useState(parseInt(localStorage.getItem(
    "personal-studio-components.accessibility.color") || "0"));
  //const root = document.documentElement;
  const body = document.body;
  if (font)
    body.style.setProperty("zoom", fontList[font]);
  else
    body.style.removeProperty("zoom");
  if (color) {
    body.classList.add("accessibility_mode");
    body.style.setProperty("color", fgList[color]);
    body.style.setProperty("background", bgList[color]);
    body.style.setProperty("--accessible-background", bgList[color]);
  }
  else {
    body.classList.remove("accessibility_mode");
    body.style.removeProperty("color");
    body.style.removeProperty("background");
    body.style.removeProperty("--accessible-background");
  }
  React.useEffect(() => {
    addEventListener("beforeprint", () => {
      if (active) {
        addEventListener("afterprint", () => {
          setActive(true);
        });
        setActive(false);
      }
    });
  }, []);
  Accessibility.onOpen = (event) => {
    event.preventDefault();
    localStorage.setItem("personal-studio-components.accessibility.active", active ? 0 : 1);
    setActive(!active);
  }
  Accessibility.onClose = (event) => {
    event.preventDefault();
    localStorage.setItem("personal-studio-components.accessibility.active", 0);
    setActive(false);
  }
  function onFont(font) {
    return (event) => {
      event.preventDefault();
      localStorage.setItem("personal-studio-components.accessibility.font", font);
      setFont(font);
    }
  }
  function onColor(color) {
    return (event) => {
      event.preventDefault();
      localStorage.setItem("personal-studio-components.accessibility.color", color);
      setColor(color);
    }
  }
  function onReset(event) {
    event.preventDefault();
    localStorage.setItem("personal-studio-components.accessibility.font", 0);
    localStorage.setItem("personal-studio-components.accessibility.color", 0);
    setFont(0);
    setColor(0);
    Accessibility.onClose(event);
  }
  const systemMenu = [];
  for (const menuItem of menu) {
    const action = new Action(menuItem);
    const name = action.name;
    const attrs = action.getAnchorAttributes();
    systemMenu.push(
      <a
        key={name}
        className={cls.accessibility_action}
        {...attrs}
      >
        {name}
      </a>)
  }
  const fontMenu = [];
  for (let i = 0; i < fontList.length; ++i) {
    const name = fontLabel[i];
    const icon = fontAbbr[i];
    const style = {};
    if (i == font) style.borderColor = fgList[color];
    fontMenu.push(
      <a
        key={name}
        aria-label={name}
        title={name}
        className={cls.accessibility_action}
        style={style}
        onClick={onFont(i)}
      >
        {icon}
      </a>)
  }
  const colorMenu = [];
  for (let i = 0; i < fgList.length; ++i) {
    const name = colorLabel[i];
    const icon = colorAbbr[i];
    const style = {};
    if (i == color) style.borderColor = fgList[color];
    style.color = fgList[i];
    style.background = bgList[i];
    colorMenu.push(
      <a
        key={name}
        aria-label={name}
        title={name}
        className={cls.accessibility_action}
        style={style}
        onClick={onColor(i)}
      >
        {icon}
      </a>)
  }
  if (!active) return null;
  return (
    <div className={cls.accessibility_navbar}>
      <span className={cls.accessibility_section}>
        <span className={cls.accessibility_label}>
          Масштаб:
        </span>
        {fontMenu}
      </span>
      <span className={cls.accessibility_section}>
        <span className={cls.accessibility_label}>
          Тема:
        </span>
        {colorMenu}
      </span>
      <span className={cls.accessibility_section}>
        <a
          aria-label="Сброс"
          title="Сброс"
          className={cls.accessibility_action}
          onClick={onReset}
        >
          Сброс
        </a>
      </span>
      {/*
        <span className={cls.accessibility_section}>
          <span className={cls.accessibility_label}>
            Меню:
          </span>
          {systemMenu}
        </span>*/}
    </div>);
}
/**
 * Меню в режиме доступности.
 * 
 * В режиме доступности доступны пункты меню: "Домой", "Поддержка", "Выключить".
 * 
 * Можно добавить свои пункты меню в массив `Accessibility.menu`
 * @type {Action[]}
 * @see {@link Action.js}
 * @example
Accessibility.menu.push(
{
      name: "Домой", // название
      href: "https://roweb.online/personal", // адрес ссылки
      target: "_blank", // если нужно открывать на новой вкладке
      onClick: (event) => {event.preventDefault();} // обработчик нажатия
});
 */
Accessibility.menu = [];
/** 
 * Пункт меню "Средства доступности" для перехода в режим доступности.
 * @type {Action}
 * @see {@link Action.js}
 */
Accessibility.OPEN = new Action(
  {
    name: "Средства доступности",
    icon: "eye",
    href: "#accessibility_mode",
    onClick: (event) => { Accessibility.onOpen(event); }
  });
/** 
 * Пункт меню "Закрыть" для закрытия панели.
 * @type {Action}
 * @see {@link Action.js}
 */
Accessibility.CLOSE = new Action(
  {
    name: "Закрыть",
    icon: "eye-invisible",
    href: "#close_accessibility_mode",
    onClick: (event) => { Accessibility.onClose(event); }
  });
